import React, { useEffect, useMemo, useRef, useState } from "react";
import { connect, ConnectedProps } from "react-redux";
import { TextInput, CustomSelect } from "..";
import type { Option } from "../Select/Select";
import { toast } from "..";
import { AlertDialog } from "@/utils/ui/AlertDialog/AlertDialog";
import {
  Search,
  Play,
  Pause,
  ChevronLeft,
  ChevronRight,
  ChevronDown,
  ChevronUp,
  Volume2,
  Check,
  ArrowUpDown,
  SlidersHorizontal,
  X,
  Plus,
  RotateCcw,
  Heart as _Heart,
} from "lucide-react";
import type { RootState } from "../../store";
import {
  getAiVoices,
  getAiLanguages,
  generateTextToAudio,
  generateTextToDialogue,
  getMedia,
} from "../../store/media/media.action";
import { callRegisterCustomMediaOnCms } from "@/store/upload/upload.action";
import { showUploadProgress } from "@/store/app/app.slice";
import { addFile } from "@/store/upload/upload.slice";
import {
  emptyMediaDetails,
  emptyAssetDetails,
} from "@/store/media/media.slice";
import type { ProductModel } from "@/database";
import {
  MULTI_SPEAKER_MODEL_IDS,
  langFlag,
  TTS_SERVICES,
  VOICE_COLORS,
} from "@/utils/constants/uiConstants";
import type { TTSVoice, TTSService } from "@/utils/constants/uiConstants";
import "./text-to-speech.scss";
import {
  useVoicePreview,
  RING_CIRC,
  RING_SIZE,
  RING_STROKE,
  RING_R,
} from "./hooks/useVoicePreview";

// ─── Component ──────────────────────────────────────────────────────────────────

interface ITextToSpeechFormOwnProps {
  closeModal: () => void;
  initialService?: TTSService | null;
  onServiceSelected?: (service: TTSService) => void;
  openUpload?: () => void;
  onNextDisabledChange?: (disabled: boolean) => void;
  onNextPendingChange?: (pending: boolean) => void;
}

type ITextToSpeechForm = ITextToSpeechFormOwnProps & ConnectedTTSProps;

interface SpeakerBlock {
  id: string;
  voice: TTSVoice | null;
  text: string;
}

interface GeneratedAudio {
  title: string;
  voiceName: string;
  voiceColor: string;
  voiceDescription: string;
  duration: number;
  generation: number;
  url?: string | null;
}

export { TTS_SERVICES };
export type { TTSService, TTSVoice };

// ─── Service Select Screen ─────────────────────────────────────────────────────

interface ITTSServiceSelect {
  onSelect: (service: TTSService) => void;
}

export const TTSServiceSelect = ({ onSelect }: ITTSServiceSelect) => (
  <div className="py-3 px-2 tts-services">
    <p className="fs-6 text-muted mb-4 tts-services__subtitle">
      Choose a text-to-speech provider to get started
    </p>
    <div className="row g-3 tts-services__grid">
      {TTS_SERVICES.map((service) => (
        <div
          key={service.id}
          className="tts-service-card"
          role="button"
          tabIndex={0}
          onClick={() => onSelect(service)}
          onKeyDown={(e) => e.key === "Enter" && onSelect(service)}
        >
          <div className="tts-service-card__header">
            <div className="tts-service-card__logo">
              <service.Logo size={40} />
            </div>
            <div className="tts-service-card__info">
              <h6 className="tts-service-card__name">{service.name}</h6>
              <span className="tts-service-card__tagline">
                {service.tagline}
              </span>
            </div>
          </div>
          <p className="tts-service-card__desc">{service.description}</p>
          <div className="tts-service-card__footer">
            <span className="tts-service-card__voices-count">
              <Volume2 size={14} /> {service.voices.length} voices
            </span>
            <span className="tts-service-card__cta">Select →</span>
          </div>
        </div>
      ))}
    </div>
  </div>
);

const TextToSpeechForm = ({
  closeModal,
  initialService,
  onServiceSelected,
  title: mediaTitle,
  Team: mediaTeam,
  bunchProductList,
  defaultTeam,
  getAiVoices,
  getAiLanguages,
  generateTextToAudio,
  generateTextToDialogue,
  aiVoices,
  aiLanguages,
  aiTextToAudio,
  aiTextToDialogue,
  singleCurrentUser,
  productList,
  callRegisterCustomMediaOnCms,
  showUploadProgress,
  addFile,
  emptyMediaDetails,
  emptyAssetDetails,
  getMedia,
  openUpload,
  onNextDisabledChange,
  onNextPendingChange,
}: ITextToSpeechForm) => {
  const [view, setView] = useState<"services" | "editor">(
    initialService ? "editor" : "services"
  );
  const [selectedService, setSelectedService] = useState<TTSService | null>(
    initialService || null
  );
  const [selectedVoice, setSelectedVoice] = useState<TTSVoice | null>(null);
  const [text, setText] = useState("");
  const [title, setTitle] = useState(mediaTitle ?? "");

  const elevenLabsKey = useMemo(() => {
    const config = defaultTeam?.ai_config?.find((c) => c.id === "eleven_labs");
    return config?.credentials?.api_key || undefined;
  }, [defaultTeam]);

  useEffect(() => {
    getAiVoices(elevenLabsKey ? { key: elevenLabsKey } : undefined);
    getAiLanguages(elevenLabsKey ? { key: elevenLabsKey } : undefined);
  }, [getAiVoices, getAiLanguages, elevenLabsKey]);

  const rpcVoices: TTSVoice[] = useMemo(() => {
    return (aiVoices?.voices?.normalized?.voices ?? []).map((v, i) => ({
      id: v.voice_id,
      name: v.name,
      description: v.description ?? "",
      gender: (v.labels?.gender === "male" ? "Male" : "Female") as
        | "Male"
        | "Female",
      language: v.labels?.language ?? "English",
      accent: v.labels?.accent ?? "American",
      category: v.category ?? "Conversational",
      age: v.labels?.age ?? "Young Adult",
      color: VOICE_COLORS[i % VOICE_COLORS.length],
      previewUrl: v.preview_url ?? undefined,
    }));
  }, [aiVoices]);

  const rpcModels = useMemo(() => {
    const rawLangs =
      aiLanguages?.languages?.raw_provider_response?.languages ?? [];
    const seen = new Set<string>();
    const models: { id: string; name: string }[] = [];
    rawLangs.forEach((lang) => {
      (lang.models ?? []).forEach((m) => {
        if (m.model_id && !seen.has(m.model_id)) {
          seen.add(m.model_id);
          models.push({ id: m.model_id, name: m.name ?? m.model_id });
        }
      });
    });
    return models;
  }, [aiLanguages]);

  const voicesLoading = aiVoices?.isLoading ?? false;
  const languagesLoading = aiLanguages?.isLoading ?? false;
  const voicesError = aiVoices?.error || null;
  const languagesError = aiLanguages?.error || null;
  const rpcBroken = !!(voicesError || languagesError);

  const formatRpcError = (error: string) =>
    error.toLowerCase().includes("unauthorized")
      ? "Unauthorized for url: https://api.elevenlabs.io/v1/models message:Invalid params or execution error"
      : error;

  useEffect(() => {
    if (voicesError) toast.fail(formatRpcError(voicesError));
  }, [voicesError]);

  useEffect(() => {
    if (languagesError) toast.fail(formatRpcError(languagesError));
  }, [languagesError]);

  // Auto-select the first RPC voice once the list arrives
  useEffect(() => {
    if (rpcVoices.length > 0 && !selectedVoice) {
      setSelectedVoice(rpcVoices[0]);
    }
  }, [rpcVoices, selectedVoice]);

  const teamOptions = (bunchProductList as ProductModel[]).filter(
    (t: any) => t?.access !== "none" && t?.access !== "read"
  );

  const [team, setTeam] = useState<string>(
    (mediaTeam || defaultTeam?.id) ?? ""
  );
  const [loading, setLoading] = useState(false);
  const [voiceSearch, setVoiceSearch] = useState("");
  const [rhsView, setRhsView] = useState<"main" | "voices">("main");
  const [selectedModel, setSelectedModel] = useState("eleven_multilingual_v2");

  const rpcLanguages = useMemo(() => {
    const rawLangs =
      aiLanguages?.languages?.raw_provider_response?.languages ?? [];
    return rawLangs
      .filter((l) => (l.models ?? []).some((m) => m.model_id === selectedModel))
      .map((l) => ({ code: l.language_id ?? "", name: l.name ?? "" }))
      .filter((l) => l.code && l.name);
  }, [aiLanguages, selectedModel]);

  const filtersRowRef = useRef<HTMLDivElement>(null);
  const [filterDropdownLeft, setFilterDropdownLeft] = useState(0);
  const [advancedSettings, setAdvancedSettings] = useState({
    stability: 30,
    similarity: 70,
    style: 60,
    speed: 1.0,
    languageOverride: true,
    selectedLanguage: "auto",
  });

  const resetSettings = () => {
    setAdvancedSettings({
      stability: 30,
      similarity: 70,
      style: 60,
      speed: 1.0,
      languageOverride: true,
      selectedLanguage: "auto",
    });
  };

  // ── Generated audio & player state ──
  const [generatedAudio, setGeneratedAudio] = useState<GeneratedAudio | null>(
    null
  );
  const [isPlaying, setIsPlaying] = useState(false);
  const [playbackProgress, setPlaybackProgress] = useState(0);
  const [audioDuration, setAudioDuration] = useState(0);
  const [showPlayer, setShowPlayer] = useState(false);
  const audioRef = useRef<HTMLAudioElement>(null);
  const textAtLastGeneration = useRef<string | null>(null);
  const settingsAtLastGeneration = useRef<string | null>(null);
  const pendingSettingsSnapshot = useRef<string | null>(null);
  const [showStaleTextDialog, setShowStaleTextDialog] = useState(false);
  const staleDialogRegenerateChosen = useRef(false);

  // ── Voice preview ──
  const {
    previewingVoiceId,
    arcRef: previewArcRef,
    barRef: previewBarRef,
    handleVoicePreview,
    stopPreview,
  } = useVoicePreview();

  useEffect(() => {
    return () => {
      stopPreview();
      const audio = audioRef.current;
      if (audio) {
        audio.pause();
        audio.src = "";
      }
    };
  }, []);

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;
    if (isPlaying) {
      audio.play().catch(() => setIsPlaying(false));
    } else {
      audio.pause();
    }
  }, [isPlaying]);

  const autoplayOnLoad = useRef(false);

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio || !generatedAudio?.url) return;
    audio.src = generatedAudio.url;
    audio.load();
    setPlaybackProgress(0);
    if (autoplayOnLoad.current) {
      autoplayOnLoad.current = false;
      audio.oncanplay = () => {
        audio.oncanplay = null;
        audio.play().catch(() => setIsPlaying(false));
        setIsPlaying(true);
      };
    } else {
      setIsPlaying(false);
    }
  }, [generatedAudio?.url]);

  const wasGenerating = useRef(false);
  const wasGeneratingDialogue = useRef(false);

  useEffect(() => {
    if (aiTextToAudio?.isLoading) {
      wasGenerating.current = true;
      onNextDisabledChange?.(true);
      return;
    }
    if (!wasGenerating.current) return;
    wasGenerating.current = false;

    if (aiTextToAudio?.result?.normalized?.url) {
      const url = aiTextToAudio.result.normalized.url;
      setGeneratedAudio((prev) => ({
        title: title || "Untitled Audio",
        voiceName: selectedVoice?.name ?? "",
        voiceColor: selectedVoice?.color ?? "#6366f1",
        voiceDescription: selectedVoice?.description ?? "",
        duration: 0,
        generation: (prev?.generation || 0) + 1,
        url,
      }));
      textAtLastGeneration.current = text;
      settingsAtLastGeneration.current = pendingSettingsSnapshot.current;
      autoplayOnLoad.current = true;
      setShowPlayer(true);
      setPlaybackProgress(0);
      setLoading(false);
      toast.success("Speech generated successfully!");
    } else {
      setLoading(false);
      toast.fail(aiTextToAudio?.error || "Failed to generate speech");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [aiTextToAudio?.isLoading]);

  useEffect(() => {
    if (aiTextToDialogue?.isLoading) {
      wasGeneratingDialogue.current = true;
      onNextDisabledChange?.(true);
      return;
    }
    if (!wasGeneratingDialogue.current) return;
    wasGeneratingDialogue.current = false;

    if (aiTextToDialogue?.result?.normalized?.url) {
      const url = aiTextToDialogue.result.normalized.url;
      setGeneratedAudio((prev) => ({
        title: title || "Untitled Audio",
        voiceName: speakers[0]?.voice?.name ?? "",
        voiceColor: speakers[0]?.voice?.color ?? "#6366f1",
        voiceDescription: speakers[0]?.voice?.description ?? "",
        duration: 0,
        generation: (prev?.generation || 0) + 1,
        url,
      }));
      textAtLastGeneration.current = speakers.map((s) => s.text).join("\0");
      settingsAtLastGeneration.current = pendingSettingsSnapshot.current;
      autoplayOnLoad.current = true;
      setShowPlayer(true);
      setPlaybackProgress(0);
      setLoading(false);
      toast.success("Speech generated successfully!");
    } else {
      setLoading(false);
      toast.fail(aiTextToDialogue?.error || "Failed to generate speech");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [aiTextToDialogue?.isLoading]);
  const [favouriteVoiceIds, setFavouriteVoiceIds] = useState<Set<string>>(
    new Set()
  );

  const toggleFavourite = (voiceId: string) => {
    setFavouriteVoiceIds((prev) => {
      const next = new Set(prev);
      if (next.has(voiceId)) {
        next.delete(voiceId);
      } else {
        next.add(voiceId);
      }
      return next;
    });
  };

  const favouriteVoices = useMemo(() => {
    if (!selectedService) return [];
    return selectedService.voices.filter((v) => favouriteVoiceIds.has(v.id));
  }, [selectedService, favouriteVoiceIds]);

  // ── Multi-speaker state (v3 model) ──
  const [speakers, setSpeakers] = useState<SpeakerBlock[]>([
    { id: "speaker-1", voice: null, text: "" },
  ]);
  const [activeSpeakerId, setActiveSpeakerId] = useState<string>("speaker-1");

  useEffect(() => {
    if (!selectedVoice) return;
    setSpeakers((prev) =>
      prev.map((s, i) => (i === 0 ? { ...s, voice: selectedVoice } : s))
    );
  }, [selectedVoice]);

  const isMultiSpeaker =
    selectedService?.id === "elevenlabs" &&
    MULTI_SPEAKER_MODEL_IDS.includes(selectedModel);

  const speaker0Text = speakers[0]?.text ?? "";

  // When in v3: keep single-speaker text in sync with speaker 1
  useEffect(() => {
    if (isMultiSpeaker) setText(speaker0Text);
  }, [isMultiSpeaker, speaker0Text]);

  // When in single-speaker: keep speaker 1 in sync with text
  useEffect(() => {
    if (!isMultiSpeaker)
      setSpeakers((prev) => prev.map((s, i) => (i === 0 ? { ...s, text } : s)));
  }, [isMultiSpeaker, text]);

  const activeSpeakerIndex = speakers.findIndex(
    (s) => s.id === activeSpeakerId
  );
  const activeSpeaker =
    speakers.find((s) => s.id === activeSpeakerId) || speakers[0];

  const addSpeaker = () => {
    const newId = `speaker-${Date.now()}`;
    setSpeakers((prev) => [
      ...prev,
      { id: newId, voice: selectedVoice, text: "" },
    ]);
    setActiveSpeakerId(newId);
  };

  const removeSpeaker = (id: string) => {
    if (speakers.length <= 1) return;
    const remaining = speakers.filter((s) => s.id !== id);
    setSpeakers(remaining);
    if (activeSpeakerId === id) {
      setActiveSpeakerId(remaining[0].id);
    }
  };

  const updateSpeakerText = (id: string, newText: string) => {
    setSpeakers((prev) => {
      const otherChars = prev
        .filter((s) => s.id !== id)
        .reduce((sum, s) => sum + s.text.trim().length, 0);
      const allowed = Math.max(0, 5000 - otherChars);
      if (newText.trim().length > allowed) return prev;
      return prev.map((s) => (s.id === id ? { ...s, text: newText } : s));
    });
  };

  const updateSpeakerVoice = (speakerId: string, voice: TTSVoice) => {
    setSpeakers((prev) =>
      prev.map((s) => (s.id === speakerId ? { ...s, voice } : s))
    );
  };

  const totalChars = isMultiSpeaker
    ? speakers.reduce((sum, s) => sum + s.text.trim().length, 0)
    : text.trim().length;

  // ── Voice filter state ──
  const [activeFilterDropdown, setActiveFilterDropdown] = useState<
    string | null
  >(null);
  const [sortBy, setSortBy] = useState<string>("Character usage");
  const [showSortDropdown, setShowSortDropdown] = useState(false);
  const [filters, setFilters] = useState<{
    language: string | null;
    accent: string | null;
    category: string | null;
    gender: string | null;
    age: string | null;
  }>({
    language: null,
    accent: null,
    category: null,
    gender: null,
    age: null,
  });

  const filterOptions = useMemo(() => {
    const voices =
      rpcVoices.length > 0 ? rpcVoices : (selectedService?.voices ?? []);
    return {
      languages: [...new Set(voices.map((v) => v.language))],
      accents: [...new Set(voices.map((v) => v.accent))],
      categories: [...new Set(voices.map((v) => v.category))],
      genders: [...new Set(voices.map((v) => v.gender))],
      ages: [...new Set(voices.map((v) => v.age))],
    };
  }, [rpcVoices, selectedService]);

  const activeFilterCount = useMemo(() => {
    return Object.values(filters).filter((v) => v !== null).length;
  }, [filters]);

  const clearAllFilters = () => {
    setFilters({
      language: null,
      accent: null,
      category: null,
      gender: null,
      age: null,
    });
    setActiveFilterDropdown(null);
  };

  const toggleFilter = (key: keyof typeof filters, value: string) => {
    setFilters((prev) => ({
      ...prev,
      [key]: prev[key] === value ? null : value,
    }));
  };

  const filteredVoices = useMemo(() => {
    const voices =
      rpcVoices.length > 0 ? rpcVoices : (selectedService?.voices ?? []);
    return voices.filter((v) => {
      const matchesSearch =
        v.name.toLowerCase().includes(voiceSearch.toLowerCase()) ||
        v.description.toLowerCase().includes(voiceSearch.toLowerCase());
      const matchesLanguage =
        !filters.language || v.language === filters.language;
      const matchesAccent = !filters.accent || v.accent === filters.accent;
      const matchesCategory =
        !filters.category || v.category === filters.category;
      const matchesGender = !filters.gender || v.gender === filters.gender;
      const matchesAge = !filters.age || v.age === filters.age;
      return (
        matchesSearch &&
        matchesLanguage &&
        matchesAccent &&
        matchesCategory &&
        matchesGender &&
        matchesAge
      );
    });
  }, [rpcVoices, selectedService, voiceSearch, filters]);

  const handleSelectService = (service: TTSService) => {
    if (onServiceSelected) {
      // Let the parent handle opening the full-screen modal
      onServiceSelected(service);
    } else {
      // Fallback: switch view internally
      setSelectedService(service);
      setSelectedVoice(service.voices[0]);
      setView("editor");
    }
  };

  const handleGenerate = () => {
    const voice = isMultiSpeaker ? speakers[0].voice : selectedVoice;

    if (isMultiSpeaker) {
      if (speakers.some((s) => !s.text.trim())) {
        toast.fail("Please enter text for all speakers");
        return;
      }
      if (speakers.some((s) => !s.voice)) {
        toast.fail("Please select a voice for all speakers");
        return;
      }
      const uniqueVoiceCount = new Set(speakers.map((s) => s.voice!.id)).size;
      if (uniqueVoiceCount > 10) {
        toast.fail("Not more than 10 unique voices are allowed");
        return;
      }
    } else {
      if (!text.trim()) {
        toast.fail("Please enter some text to convert");
        return;
      }
      if (!voice) {
        toast.fail("Please select a voice");
        return;
      }
    }
    if (!title.trim()) {
      toast.fail("Please enter a title for the audio");
      return;
    }

    setLoading(true);

    pendingSettingsSnapshot.current = JSON.stringify({
      voiceId: isMultiSpeaker
        ? speakers.map((s) => s.voice?.id)
        : selectedVoice?.id,
      model: selectedModel,
      languageOverride: advancedSettings.languageOverride,
      selectedLanguage: advancedSettings.selectedLanguage,
      stability: advancedSettings.stability,
      similarity: advancedSettings.similarity,
      style: advancedSettings.style,
      speed: advancedSettings.speed,
    });

    if (isMultiSpeaker) {
      generateTextToDialogue({
        inputs: speakers.map((s) => ({
          text: s.text,
          voice_id: s.voice!.id,
        })),
        model_id: selectedModel,
        output_format: "mp3_44100_128",
        language_code: advancedSettings.languageOverride
          ? advancedSettings.selectedLanguage === "auto"
            ? "en"
            : advancedSettings.selectedLanguage
          : "en",
        raw: false,
        return_url: true,
        sid: `tts_${Date.now()}`,
        ...(elevenLabsKey ? { key: elevenLabsKey } : {}),
        // product: team,
      });
    } else {
      generateTextToAudio({
        text,
        voice_id: voice!.id,
        model_id: selectedModel,
        language_code: advancedSettings.languageOverride
          ? advancedSettings.selectedLanguage === "auto"
            ? "en"
            : advancedSettings.selectedLanguage
          : "en",
        output_format: "mp3_44100_128",
        voice_settings: {
          stability: advancedSettings.stability / 100,
          similarity_boost: advancedSettings.similarity / 100,
          style: advancedSettings.style / 100,
          speed: advancedSettings.speed,
          use_speaker_boost: true,
        },
        raw: false,
        return_url: true,
        sid: `tts_${Date.now()}`,
        ...(elevenLabsKey ? { key: elevenLabsKey } : {}),
        // product: team,
      });
    }
  };

  useEffect(() => {
    onNextDisabledChange?.(!generatedAudio?.url);
  }, [generatedAudio?.url]);

  const handleNextProceed = () => {
    if (!generatedAudio?.url) return;
    onNextPendingChange?.(true);
    showUploadProgress();
    emptyMediaDetails();
    emptyAssetDetails();
    callRegisterCustomMediaOnCms(
      {
        type: "httpdownload",
        data: { content: "audio", downloads: { url: generatedAudio.url } },
        name: "media",
        title,
        downloads: { url: generatedAudio.url },
        source: "texttospeech",
        asset_type: "audio",
        team,
      },
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (response: any) => {
        if (response?.status === 200) {
          if (response?.data?.preset_config?.meta) {
            localStorage.setItem(
              "metaPresetId",
              response.data.preset_config.meta
            );
          } else {
            const metaId = productList?.find(
              (item: any) => item?.id === singleCurrentUser?.team
            )?.presets?.meta;
            localStorage.setItem("metaPresetId", metaId);
          }
          if (response?.data?.preset_config?.social) {
            localStorage.setItem(
              "socialPresetId",
              response.data.preset_config.social
            );
          } else {
            const socialId = productList?.find(
              (item: any) => item?.id === singleCurrentUser?.team
            )?.presets?.social;
            localStorage.setItem("socialPresetId", socialId);
          }
          setTimeout(() => {
            getMedia({ id: response.data.id });
          }, 200);
          addFile({
            key: response.data.id,
            file: {
              id: response.data.id,
              name: response.data.title,
              identifier: response.data.id,
              progress: 0,
              isUploadedSuccessfully: false,
              type: response.data.type,
              error: null,
            },
          });
          closeModal();
          openUpload?.();
        } else {
          onNextPendingChange?.(false);
          toast.fail(response?.error || "Failed to register media");
        }
      }
    );
  };

  const handleNext = () => {
    if (!generatedAudio?.url) return;
    const currentTextSnapshot = isMultiSpeaker
      ? speakers.map((s) => s.text).join("\0")
      : text.trim();
    const currentSettingsSnapshot = JSON.stringify({
      voiceId: isMultiSpeaker
        ? speakers.map((s) => s.voice?.id)
        : selectedVoice?.id,
      model: selectedModel,
      languageOverride: advancedSettings.languageOverride,
      selectedLanguage: advancedSettings.selectedLanguage,
      stability: advancedSettings.stability,
      similarity: advancedSettings.similarity,
      style: advancedSettings.style,
      speed: advancedSettings.speed,
    });
    const isStale =
      (textAtLastGeneration.current !== null &&
        currentTextSnapshot !== textAtLastGeneration.current.trim()) ||
      (settingsAtLastGeneration.current !== null &&
        currentSettingsSnapshot !== settingsAtLastGeneration.current);
    if (isStale) {
      setShowStaleTextDialog(true);
      return;
    }
    handleNextProceed();
  };

  useEffect(() => {
    const onNext = () => handleNext();
    window.addEventListener("customNextClick", onNext);
    return () => window.removeEventListener("customNextClick", onNext);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    generatedAudio?.url,
    title,
    text,
    speakers,
    isMultiSpeaker,
    selectedVoice,
    selectedModel,
    advancedSettings,
  ]);

  const handlePlayPause = () => {
    setIsPlaying(!isPlaying);
  };

  const formatTime = (seconds: number) => {
    const m = Math.floor(seconds / 60);
    const s = Math.floor(seconds % 60);
    return `${m}:${s.toString().padStart(2, "0")}`;
  };

  // ── Service Selection View ──────────────────────────────────────────────────

  if (view === "services") {
    return <TTSServiceSelect onSelect={handleSelectService} />;
  }

  // ── Editor View ─────────────────────────────────────────────────────────────

  return (
    <div className="tts-editor">
      {/* ── Main Split Layout ── */}
      <div className="d-flex flex-column flex-lg-row flex-grow-1 border rounded overflow-hidden shadow-sm tts-editor__body">
        {/* LHS - Text Input */}
        <div className="d-flex flex-column flex-grow-1 border-end min-w-0 bg-white tts-editor__text-panel">
          {/* ── Title & Description ──
              Compact horizontal row: label sits beside the input as a quiet
              prefix instead of a stacked uppercase header. Saves ~24px of
              vertical room so the RHS panel below has space to fit without
              an inner scrollbar. */}
          <div className="tts-editor__meta-fields">
            <div className="tts-editor__meta-field">
              <TextInput
                label="Title"
                placeholder="Enter audio title…"
                value={title}
                onChange={(e) => {
                  if (e.target.value.length <= 100) setTitle(e.target.value);
                }}
                characterLimit={100}
                showRequired={true}
              />
            </div>
            <div className="tts-editor__meta-field">
              <CustomSelect
                label="Team/Property"
                value={team}
                options={teamOptions.map((t: ProductModel) => ({
                  label: t.name,
                  value: t.id,
                }))}
                handleChange={(option: Option | null) =>
                  setTeam(((option as Option)?.value as string) ?? "")
                }
                placeholder="Select team…"
              />
            </div>
          </div>

          {/* ── Divider ── */}
          <div className="d-none tts-editor__lhs-divider" />

          {isMultiSpeaker ? (
            /* ── Multi-Speaker Mode (v3) ── */
            <>
              <div className="tts-editor__speakers-scroll">
                {speakers.map((speaker, index) => {
                  const isActive = activeSpeakerId === speaker.id;
                  return (
                    <div
                      key={speaker.id}
                      className={`tts-editor__speaker-block ${isActive ? "tts-editor__speaker-block--active" : ""}`}
                      onClick={() => setActiveSpeakerId(speaker.id)}
                    >
                      <div className="tts-editor__speaker-header">
                        <button
                          className={`tts-editor__speaker-voice-chip ${isActive ? "tts-editor__speaker-voice-chip--active" : ""}`}
                          onClick={(e) => {
                            e.stopPropagation();
                            setActiveSpeakerId(speaker.id);
                          }}
                        >
                          {speaker.voice ? (
                            <>
                              <span
                                className="tts-editor__voice-dot"
                                style={{ background: speaker.voice.color }}
                              />
                              <span className="tts-editor__speaker-chip-name">
                                {speaker.voice.name}
                              </span>
                            </>
                          ) : (
                            <span>Select a voice →</span>
                          )}
                        </button>
                        {speakers.length > 1 && (
                          <button
                            className="tts-editor__speaker-remove"
                            onClick={(e) => {
                              e.stopPropagation();
                              removeSpeaker(speaker.id);
                            }}
                            title="Remove speaker"
                          >
                            <X size={16} />
                          </button>
                        )}
                      </div>

                      <textarea
                        className="tts-editor__speaker-textarea"
                        placeholder="Type your text with audio tags like [laughs] to turn into expressive speech..."
                        value={speaker.text}
                        onChange={(e) =>
                          updateSpeakerText(speaker.id, e.target.value)
                        }
                        onFocus={() => setActiveSpeakerId(speaker.id)}
                        rows={3}
                      />
                      {index < speakers.length - 1 && (
                        <div className="tts-editor__speaker-divider" />
                      )}
                    </div>
                  );
                })}

                <button
                  className="tts-editor__add-speaker"
                  onClick={addSpeaker}
                >
                  <Plus size={16} />
                  Add speaker
                </button>
              </div>

              <div className="tts-editor__text-footer">
                <span className="tts-editor__char-count">
                  {totalChars} / 5,000 characters
                </span>
                <div className="tts-editor__footer-right">
                  {isMultiSpeaker && (
                    <span className="tts-editor__v3-badge">V3</span>
                  )}
                  {totalChars > 0 && (
                    <span className="tts-editor__est-duration">
                      ~
                      {Math.max(
                        1,
                        Math.ceil(totalChars / (15 * advancedSettings.speed))
                      )}
                      s estimated
                    </span>
                  )}
                </div>
              </div>

              <div className="tts-editor__actions">
                <button
                  type="button"
                  className="btn btn-dark d-flex align-items-center gap-1"
                  onClick={handleGenerate}
                  disabled={loading || rpcBroken}
                  style={{
                    fontSize: "0.7rem",
                    padding: "3px 10px",
                    borderRadius: "12px",
                    fontWeight: "600",
                    letterSpacing: "0.3px",
                    whiteSpace: "nowrap",
                    flexShrink: 0,
                  }}
                >
                  {loading ? (
                    <>
                      <span
                        className="spinner-border spinner-border-sm me-2"
                        role="status"
                        aria-hidden="true"
                      />
                      Generating…
                    </>
                  ) : generatedAudio ? (
                    "Regenerate speech"
                  ) : (
                    "Generate speech"
                  )}
                </button>
              </div>
            </>
          ) : (
            /* ── Single-Speaker Mode (v2 / other) ── */
            <>
              <div
                className="d-flex align-items-center px-4 py-2 border-bottom border-light-subtle tts-editor__text-header"
                style={{ minHeight: "42px" }}
              >
                {selectedVoice && (
                  <div
                    className="d-inline-flex align-items-center gap-2 bg-light border border-light-subtle rounded-pill px-3 py-1 text-dark tts-editor__selected-voice-chip"
                    style={{ fontSize: "0.82rem", fontWeight: 500 }}
                  >
                    <span
                      className="flex-shrink-0 rounded-circle tts-editor__voice-dot"
                      style={{
                        width: "8px",
                        height: "8px",
                        background: selectedVoice.color,
                      }}
                    />
                    <span>{selectedVoice.name}</span>
                  </div>
                )}
              </div>
              <textarea
                className="form-control border-0 px-4 py-3 flex-grow-1 rounded-0 shadow-none tts-editor__textarea"
                style={{ resize: "none", fontSize: "0.9rem", lineHeight: 1.6 }}
                placeholder="Type or paste the text you'd like to convert to speech…"
                value={text}
                onChange={(e) => {
                  if (e.target.value.trim().length <= 5000)
                    setText(e.target.value);
                }}
              />
              <div className="d-flex align-items-center justify-content-between px-4 py-2 bg-light border-top border-light-subtle tts-editor__text-footer">
                <span
                  className="text-muted tts-editor__char-count"
                  style={{ fontSize: "0.75rem" }}
                >
                  {text.trim().length} / 5,000 characters
                </span>
                {text.trim().length > 0 && (
                  <span
                    className="text-secondary fw-medium tts-editor__est-duration"
                    style={{ fontSize: "0.75rem" }}
                  >
                    ~
                    {Math.max(
                      1,
                      Math.ceil(
                        text.trim().length / (15 * advancedSettings.speed)
                      )
                    )}
                    s estimated
                  </span>
                )}
              </div>

              <div className="d-flex align-items-center justify-content-end px-4 py-3 bg-light border-top border-light-subtle tts-editor__actions">
                <button
                  type="button"
                  className="btn btn-dark d-flex align-items-center gap-1"
                  onClick={handleGenerate}
                  disabled={loading || rpcBroken}
                  style={{
                    fontSize: "0.7rem",
                    padding: "3px 10px",
                    borderRadius: "12px",
                    fontWeight: "600",
                    letterSpacing: "0.3px",
                    whiteSpace: "nowrap",
                    flexShrink: 0,
                  }}
                >
                  {loading ? (
                    <>
                      <span
                        className="spinner-border spinner-border-sm me-2"
                        role="status"
                        aria-hidden="true"
                      />
                      Generating…
                    </>
                  ) : generatedAudio ? (
                    "Regenerate speech"
                  ) : (
                    "Generate speech"
                  )}
                </button>
              </div>
            </>
          )}
        </div>

        {/* RHS - Settings Panel */}
        <div className="tts-editor__voice-panel">
          {/* ── RHS Panel Header ──
              Aligned to the LHS meta-fields row so the band reads as a
              single header strip across both panels. Title is bumped to
              size up "Settings" as a section heading; the service badge
              promotes the platform name from a quiet tag to a context cue. */}
          <div className="tts-editor__rhs-panel-header">
            <h2 className="tts-editor__rhs-panel-title">Settings</h2>
            {selectedService && (
              <div className="tts-editor__service-badge">
                <selectedService.Logo size={20} />
                <span>{selectedService.name}</span>
              </div>
            )}
          </div>

          {rhsView === "main" ? (
            <div className="tts-editor__rhs-main">
              {/* ── Voice Section ── */}
              <div className="tts-editor__rhs-section">
                <div className="d-flex align-items-center justify-content-between mb-2 tts-editor__rhs-voice-header">
                  <label
                    className="text-uppercase text-dark fw-bold mb-0 tts-editor__rhs-label"
                    style={{ fontSize: "0.75rem", letterSpacing: "0.5px" }}
                  >
                    Voice
                  </label>
                  <div className="tts-editor__rhs-voice-header-right">
                    {isMultiSpeaker && (
                      <span className="tts-editor__speaker-indicator">
                        Speaker {activeSpeakerIndex + 1}
                      </span>
                    )}
                  </div>
                </div>

                {/* Selected Voice Card */}
                {(() => {
                  const currentVoice = isMultiSpeaker
                    ? activeSpeaker?.voice
                    : selectedVoice;
                  if (voicesLoading && !currentVoice) {
                    return (
                      <div className="d-flex align-items-center p-3 border border-light-subtle rounded bg-white tts-editor__voice-card tts-editor__voice-card--empty">
                        <span
                          className="spinner-border spinner-border-sm me-2"
                          role="status"
                          aria-hidden="true"
                        />
                        <span className="text-muted">Loading voices…</span>
                      </div>
                    );
                  }
                  if (!currentVoice) {
                    return (
                      <button
                        className="d-flex align-items-center p-3 border border-light-subtle rounded text-muted cursor-pointer bg-white tts-editor__voice-card tts-editor__voice-card--empty"
                        style={{ borderStyle: "dashed" }}
                        onClick={() => setRhsView("voices")}
                        disabled={rpcBroken}
                      >
                        <span>Select voice</span>
                        <ChevronRight size={16} />
                      </button>
                    );
                  }
                  return (
                    <div className="tts-editor__voice-card">
                      <div
                        className="tts-editor__voice-card-avatar"
                        style={{
                          background: `linear-gradient(135deg, ${currentVoice.color}cc, ${currentVoice.color})`,
                        }}
                      >
                        {currentVoice.name.charAt(0)}
                      </div>
                      <div className="tts-editor__voice-card-info">
                        <span className="tts-editor__voice-card-name">
                          {currentVoice.name}
                        </span>
                        <span className="tts-editor__voice-card-desc">
                          {currentVoice.description}
                        </span>
                        {previewingVoiceId === currentVoice.id && (
                          <div className="tts-editor__voice-card-progress-track">
                            <div
                              ref={previewBarRef}
                              className="tts-editor__voice-card-progress-bar"
                            />
                          </div>
                        )}
                      </div>
                      <div className="tts-editor__voice-card-actions">
                        {/* TODO: favourites section — re-enable when ready
                        <button
                          className={`tts-editor__voice-card-fav ${favouriteVoiceIds.has(currentVoice.id) ? "tts-editor__voice-card-fav--active" : ""}`}
                          onClick={() => toggleFavourite(currentVoice.id)}
                          title={
                            favouriteVoiceIds.has(currentVoice.id)
                              ? "Remove from favourites"
                              : "Add to favourites"
                          }
                        >
                          <Heart
                            size={14}
                            fill={
                              favouriteVoiceIds.has(currentVoice.id)
                                ? "currentColor"
                                : "none"
                            }
                          />
                        </button>
                        */}
                        <button
                          className={`tts-editor__voice-card-play ${previewingVoiceId === currentVoice.id ? "tts-editor__voice-card-play--active" : ""}`}
                          onClick={(e) =>
                            handleVoicePreview(
                              {
                                id: currentVoice.id,
                                previewUrl: currentVoice.previewUrl,
                              },
                              e
                            )
                          }
                          title="Preview voice"
                        >
                          {previewingVoiceId === currentVoice.id ? (
                            <Pause size={14} />
                          ) : (
                            <Play size={14} />
                          )}
                        </button>
                      </div>
                    </div>
                  );
                })()}

                {/* Quick voice picks */}
                <div className="tts-editor__quick-voices">
                  {favouriteVoices.slice(0, 3).map((v) => {
                    const isActive = isMultiSpeaker
                      ? activeSpeaker?.voice?.id === v.id
                      : selectedVoice?.id === v.id;
                    return (
                      <button
                        key={v.id}
                        className={`tts-editor__quick-voice ${isActive ? "tts-editor__quick-voice--active" : ""}`}
                        onClick={() => {
                          if (isMultiSpeaker) {
                            updateSpeakerVoice(activeSpeakerId, v);
                          } else {
                            setSelectedVoice(v);
                          }
                        }}
                      >
                        <span
                          className="tts-editor__voice-dot"
                          style={{ background: v.color }}
                        />
                        {v.name}
                      </button>
                    );
                  })}
                  {favouriteVoices.length > 3 && (
                    <button
                      className="tts-editor__quick-voice tts-editor__quick-voice--more"
                      onClick={() => setRhsView("voices")}
                    >
                      +{favouriteVoices.length - 3} more
                    </button>
                  )}
                  <button
                    className="tts-editor__quick-voice tts-editor__quick-voice--browse"
                    onClick={() => setRhsView("voices")}
                    disabled={rpcBroken}
                  >
                    <Search size={12} />
                    All voices
                  </button>
                </div>
              </div>

              {/* ── Model Selection ── */}
              <div className="tts-editor__rhs-section">
                <CustomSelect
                  label="Model"
                  value={selectedModel}
                  options={rpcModels.map((m) => ({
                    label: m.name,
                    value: m.id,
                  }))}
                  handleChange={(opt: any) => {
                    const switchingToMultiSpeaker =
                      MULTI_SPEAKER_MODEL_IDS.includes(opt.value);
                    const leavingMultiSpeaker =
                      isMultiSpeaker &&
                      !MULTI_SPEAKER_MODEL_IDS.includes(opt.value);
                    if (switchingToMultiSpeaker) {
                      setSpeakers((prev) =>
                        prev.map((s, i) =>
                          i === 0 ? { ...s, text: text || s.text } : s
                        )
                      );
                    }
                    if (leavingMultiSpeaker) {
                      setText(speakers[0]?.text ?? "");
                    }
                    setSelectedModel(opt.value);
                    setAdvancedSettings((s) => ({
                      ...s,
                      selectedLanguage: "auto",
                    }));
                  }}
                  placeholder="Select model"
                  showHeader={false}
                  isSearchable={false}
                />
              </div>

              {/* ── Voice Settings (inline) ── */}
              <div className="tts-editor__rhs-section">
                {/* Speed — ElevenLabs: 0.7–1.2 */}
                {selectedService?.id === "elevenlabs" &&
                  !isMultiSpeaker &&
                  (() => {
                    const speedPct = Math.round(
                      ((advancedSettings.speed - 0.7) / (1.2 - 0.7)) * 100
                    );
                    return (
                      <div className="mb-4 tts-editor__slider-group">
                        <label className="tts-editor__slider-title">
                          Speed
                        </label>
                        <div className="tts-editor__slider-labels">
                          <span>Slower</span>
                          <span>Faster</span>
                        </div>
                        <div className="tts-editor__slider-wrap">
                          <span
                            className="tts-editor__slider-bubble"
                            style={{
                              left: `calc(${speedPct}% + ${8 - speedPct * 0.16}px)`,
                            }}
                          >
                            {advancedSettings.speed.toFixed(2)}x
                          </span>
                          <input
                            type="range"
                            min="0.7"
                            max="1.2"
                            step="0.05"
                            value={advancedSettings.speed}
                            onChange={(e) =>
                              setAdvancedSettings((s) => ({
                                ...s,
                                speed: Number(e.target.value),
                              }))
                            }
                            className="form-range tts-editor__slider"
                          />
                        </div>
                      </div>
                    );
                  })()}

                {/* Stability, Similarity & Style (v2 models only, not multi-speaker) */}
                {selectedModel.includes("v2") && !isMultiSpeaker && (
                  <>
                    <div className="mb-4 tts-editor__slider-group">
                      <div className="tts-editor__slider-title-row">
                        <label className="tts-editor__slider-title">
                          Stability
                        </label>
                        {advancedSettings.stability < 30 && (
                          <span className="tts-editor__slider-warning">
                            Under 30% may lead to instability
                          </span>
                        )}
                      </div>
                      <div className="tts-editor__slider-labels">
                        <span>More variable</span>
                        <span>More stable</span>
                      </div>
                      <div className="tts-editor__slider-wrap">
                        <span
                          className="tts-editor__slider-bubble"
                          style={{
                            left: `calc(${advancedSettings.stability}% + ${8 - advancedSettings.stability * 0.16}px)`,
                          }}
                        >
                          {advancedSettings.stability}%
                        </span>
                        <input
                          type="range"
                          min="0"
                          max="100"
                          value={advancedSettings.stability}
                          onChange={(e) =>
                            setAdvancedSettings((s) => ({
                              ...s,
                              stability: Number(e.target.value),
                            }))
                          }
                          className="form-range tts-editor__slider"
                        />
                      </div>
                    </div>
                    <div className="mb-4 tts-editor__slider-group">
                      <label className="tts-editor__slider-title">
                        Similarity
                      </label>
                      <div className="tts-editor__slider-labels">
                        <span>Low</span>
                        <span>High</span>
                      </div>
                      <div className="tts-editor__slider-wrap">
                        <span
                          className="tts-editor__slider-bubble"
                          style={{
                            left: `calc(${advancedSettings.similarity}% + ${8 - advancedSettings.similarity * 0.16}px)`,
                          }}
                        >
                          {advancedSettings.similarity}%
                        </span>
                        <input
                          type="range"
                          min="0"
                          max="100"
                          value={advancedSettings.similarity}
                          onChange={(e) =>
                            setAdvancedSettings((s) => ({
                              ...s,
                              similarity: Number(e.target.value),
                            }))
                          }
                          className="form-range tts-editor__slider"
                        />
                      </div>
                    </div>
                    <div className="mb-4 tts-editor__slider-group">
                      <div className="tts-editor__slider-title-row">
                        <label className="tts-editor__slider-title">
                          Style Exaggeration
                        </label>
                        {advancedSettings.style > 50 && (
                          <span className="tts-editor__slider-warning">
                            Over 50% may lead to instability
                          </span>
                        )}
                      </div>
                      <div className="tts-editor__slider-labels">
                        <span>None</span>
                        <span>Exaggerated</span>
                      </div>
                      <div className="tts-editor__slider-wrap">
                        <span
                          className="tts-editor__slider-bubble"
                          style={{
                            left: `calc(${advancedSettings.style}% + ${8 - advancedSettings.style * 0.16}px)`,
                          }}
                        >
                          {advancedSettings.style}%
                        </span>
                        <input
                          type="range"
                          min="0"
                          max="100"
                          value={advancedSettings.style}
                          onChange={(e) =>
                            setAdvancedSettings((s) => ({
                              ...s,
                              style: Number(e.target.value),
                            }))
                          }
                          className="form-range tts-editor__slider"
                        />
                      </div>
                    </div>
                  </>
                )}

                {/* Language Override */}
                <div className="tts-editor__language-override">
                  <div className="tts-editor__language-override-row">
                    <label className="tts-editor__slider-title">
                      Language Override
                    </label>
                    <button
                      className={`tts-editor__toggle ${advancedSettings.languageOverride ? "tts-editor__toggle--on" : ""}`}
                      onClick={() =>
                        setAdvancedSettings((s) => ({
                          ...s,
                          languageOverride: !s.languageOverride,
                        }))
                      }
                      role="switch"
                      aria-checked={advancedSettings.languageOverride}
                    >
                      <span className="tts-editor__toggle-thumb" />
                    </button>
                  </div>
                  {advancedSettings.languageOverride && (
                    <CustomSelect
                      value={advancedSettings.selectedLanguage}
                      options={[
                        { label: "Auto (Recommended)", value: "auto" },
                        ...rpcLanguages.map((l) => ({
                          label: l.name,
                          value: l.code,
                        })),
                      ]}
                      handleChange={(opt: any) =>
                        setAdvancedSettings((s) => ({
                          ...s,
                          selectedLanguage: opt.value,
                        }))
                      }
                      placeholder="Select language"
                      showHeader={false}
                      loader={languagesLoading}
                      customOptionRenderer={(opt) => (
                        <span
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "8px",
                          }}
                        >
                          <span style={{ fontSize: "1.1em", lineHeight: 1 }}>
                            {opt.value === "auto"
                              ? "🌐"
                              : langFlag(String(opt.value))}
                          </span>
                          {opt.label}
                        </span>
                      )}
                      customValueRenderer={(opt) => (
                        <span
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "6px",
                          }}
                        >
                          <span style={{ fontSize: "1.1em", lineHeight: 1 }}>
                            {opt.value === "auto"
                              ? "🌐"
                              : langFlag(String(opt.value))}
                          </span>
                          {opt.label}
                        </span>
                      )}
                    />
                  )}
                </div>

                {/* Reset values */}
                <button
                  className="tts-editor__reset-btn"
                  onClick={resetSettings}
                >
                  <RotateCcw size={14} />
                  <span>Reset values</span>
                </button>
              </div>
            </div>
          ) : rhsView === "voices" ? (
            /* ── Voice Browser Sub-View ── */
            <div className="tts-editor__voices-view">
              <div className="tts-editor__advanced-header">
                <button
                  className="tts-editor__advanced-back"
                  onClick={() => { stopPreview(); setRhsView("main"); }}
                >
                  <ChevronLeft size={16} />
                  <span>Back</span>
                </button>
                <h6 className="tts-editor__advanced-title">All Voices</h6>
              </div>

              <div className="tts-editor__voices-browser">
                {/* Search bar with sort */}
                <div className="tts-editor__voice-search">
                  <Search size={14} className="tts-editor__voice-search-icon" />
                  <input
                    type="text"
                    placeholder="Search voices..."
                    value={voiceSearch}
                    onChange={(e) => setVoiceSearch(e.target.value)}
                  />
                  <div className="tts-editor__sort-wrapper">
                    <button
                      className={`tts-editor__sort-btn ${showSortDropdown ? "tts-editor__sort-btn--active" : ""}`}
                      onClick={() => {
                        setShowSortDropdown(!showSortDropdown);
                        setActiveFilterDropdown(null);
                      }}
                      title="Sort voices"
                    >
                      <ArrowUpDown size={15} />
                    </button>
                    {showSortDropdown && (
                      <div className="tts-editor__sort-dropdown">
                        {[
                          "Trending",
                          "Latest",
                          "Most users",
                          "Character usage",
                        ].map((option) => (
                          <button
                            key={option}
                            className={`tts-editor__sort-option ${sortBy === option ? "tts-editor__sort-option--active" : ""}`}
                            onClick={() => {
                              setSortBy(option);
                              setShowSortDropdown(false);
                            }}
                          >
                            <span>{option}</span>
                            {sortBy === option && <Check size={14} />}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                </div>

                {/* Filters */}
                <div className="tts-editor__filters-wrapper">
                  <div
                    className="tts-editor__voice-filters"
                    ref={filtersRowRef}
                  >
                    {[
                      {
                        key: "language" as const,
                        label: "Languages",
                        options: filterOptions.languages,
                      },
                      {
                        key: "accent" as const,
                        label: "Accent",
                        options: filterOptions.accents,
                      },
                      {
                        key: "category" as const,
                        label: "Category",
                        options: filterOptions.categories,
                      },
                      {
                        key: "gender" as const,
                        label: "Gender",
                        options: filterOptions.genders,
                      },
                      {
                        key: "age" as const,
                        label: "Age",
                        options: filterOptions.ages,
                      },
                    ].map((filter) => (
                      <button
                        key={filter.key}
                        className={`tts-editor__filter-chip ${filters[filter.key] ? "tts-editor__filter-chip--active" : ""} ${activeFilterDropdown === filter.key ? "tts-editor__filter-chip--open" : ""}`}
                        onClick={(e) => {
                          if (activeFilterDropdown === filter.key) {
                            setActiveFilterDropdown(null);
                          } else {
                            const btn = e.currentTarget;
                            const row = filtersRowRef.current;
                            if (row) {
                              setFilterDropdownLeft(
                                btn.offsetLeft - row.scrollLeft
                              );
                            }
                            setActiveFilterDropdown(filter.key);
                          }
                          setShowSortDropdown(false);
                        }}
                      >
                        {filters[filter.key] ? (
                          <>
                            <span>{filters[filter.key]}</span>
                            <X
                              size={12}
                              className="tts-editor__filter-chip-clear"
                              onClick={(e) => {
                                e.stopPropagation();
                                toggleFilter(
                                  filter.key,
                                  filters[filter.key] as string
                                );
                              }}
                            />
                          </>
                        ) : (
                          <span>+ {filter.label}</span>
                        )}
                      </button>
                    ))}
                    <button
                      className={`tts-editor__filter-settings ${activeFilterCount > 0 ? "tts-editor__filter-settings--active" : ""}`}
                      onClick={clearAllFilters}
                      title={
                        activeFilterCount > 0 ? "Clear all filters" : "Filters"
                      }
                    >
                      <SlidersHorizontal size={14} />
                    </button>
                  </div>

                  {activeFilterDropdown &&
                    (() => {
                      const filterDefs = [
                        {
                          key: "language" as const,
                          options: filterOptions.languages,
                        },
                        {
                          key: "accent" as const,
                          options: filterOptions.accents,
                        },
                        {
                          key: "category" as const,
                          options: filterOptions.categories,
                        },
                        {
                          key: "gender" as const,
                          options: filterOptions.genders,
                        },
                        { key: "age" as const, options: filterOptions.ages },
                      ];
                      const activeFilter = filterDefs.find(
                        (f) => f.key === activeFilterDropdown
                      );
                      if (!activeFilter) return null;
                      return (
                        <div
                          className="tts-editor__filter-panel"
                          style={{ left: filterDropdownLeft }}
                        >
                          {activeFilter.options.map((opt: string) => (
                            <button
                              key={opt}
                              className={`tts-editor__filter-option ${filters[activeFilter.key] === opt ? "tts-editor__filter-option--active" : ""}`}
                              onClick={() => {
                                toggleFilter(activeFilter.key, opt);
                                setActiveFilterDropdown(null);
                              }}
                            >
                              <span>{opt}</span>
                              {filters[activeFilter.key] === opt && (
                                <Check size={13} />
                              )}
                            </button>
                          ))}
                        </div>
                      );
                    })()}
                </div>

                {activeFilterCount > 0 && (
                  <div className="tts-editor__filter-summary">
                    <span>
                      {filteredVoices.length} voice
                      {filteredVoices.length !== 1 ? "s" : ""} found
                    </span>
                    <button
                      className="tts-editor__filter-clear-all"
                      onClick={clearAllFilters}
                    >
                      <X size={12} />
                      Clear all filters
                    </button>
                  </div>
                )}

                {/* Voice list */}
                <div className="tts-editor__voice-list">
                  {voicesLoading && (
                    <div className="tts-editor__voice-empty">
                      <span
                        className="spinner-border spinner-border-sm me-2"
                        role="status"
                        aria-hidden="true"
                      />
                      Loading voices…
                    </div>
                  )}
                  {!voicesLoading && filteredVoices.length === 0 && (
                    <div className="tts-editor__voice-empty">
                      No voices match your search
                    </div>
                  )}
                  {filteredVoices.map((voice) => {
                    const isSelected = isMultiSpeaker
                      ? activeSpeaker?.voice?.id === voice.id
                      : selectedVoice?.id === voice.id;

                    const handleVoiceClick = () => {
                      if (isMultiSpeaker) {
                        updateSpeakerVoice(activeSpeakerId, voice);
                      } else {
                        setSelectedVoice(voice);
                      }
                      setRhsView("main");
                    };
                    return (
                      <div
                        key={voice.id}
                        className={`tts-editor__voice-item ${isSelected ? "tts-editor__voice-item--selected" : ""}`}
                        role="button"
                        tabIndex={0}
                        onClick={handleVoiceClick}
                        onKeyDown={(e) =>
                          e.key === "Enter" && handleVoiceClick()
                        }
                      >
                        <div
                          className="tts-editor__voice-avatar"
                          style={{
                            background: `linear-gradient(135deg, ${voice.color}cc, ${voice.color})`,
                            position: "relative",
                            overflow: "visible",
                          }}
                        >
                          {voice.name.charAt(0)}
                          {previewingVoiceId === voice.id && (
                            <svg
                              width={RING_SIZE}
                              height={RING_SIZE}
                              style={{
                                position: "absolute",
                                top: "50%",
                                left: "50%",
                                transform:
                                  "translate(-50%, -50%) rotate(-90deg)",
                                pointerEvents: "none",
                              }}
                            >
                              <circle
                                cx={RING_SIZE / 2}
                                cy={RING_SIZE / 2}
                                r={RING_R}
                                fill="none"
                                stroke="rgba(0,0,0,0.15)"
                                strokeWidth={RING_STROKE}
                              />
                              <circle
                                ref={previewArcRef}
                                cx={RING_SIZE / 2}
                                cy={RING_SIZE / 2}
                                r={RING_R}
                                fill="none"
                                stroke="#444"
                                strokeWidth={RING_STROKE}
                                strokeLinecap="round"
                                strokeDasharray={RING_CIRC}
                                strokeDashoffset={RING_CIRC}
                              />
                            </svg>
                          )}
                        </div>
                        <div className="tts-editor__voice-meta">
                          <span className="tts-editor__voice-name">
                            {voice.name}
                          </span>
                          <span className="tts-editor__voice-desc">
                            {voice.description}
                          </span>
                        </div>
                        <div className="tts-editor__voice-actions">
                          {/* TODO: favourites section — re-enable when ready
                          <button
                            className={`tts-editor__voice-fav-btn ${isFav ? "tts-editor__voice-fav-btn--active" : ""}`}
                            onClick={(e) => {
                              e.stopPropagation();
                              toggleFavourite(voice.id);
                            }}
                            title={
                              isFav
                                ? "Remove from favourites"
                                : "Add to favourites"
                            }
                          >
                            <Heart
                              size={13}
                              fill={isFav ? "currentColor" : "none"}
                            />
                          </button>
                          */}
                          {isSelected ? (
                            <span className="tts-editor__voice-check">
                              <Check size={16} />
                            </span>
                          ) : (
                            <button
                              className="tts-editor__voice-play"
                              title={`Preview ${voice.name}`}
                              onClick={(e) => handleVoicePreview(voice, e)}
                              disabled={!voice.previewUrl}
                            >
                              {previewingVoiceId === voice.id ? (
                                <Pause size={14} />
                              ) : (
                                <Play size={14} />
                              )}
                            </button>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          ) : null}
        </div>
      </div>

      {/* ── Podcast Player Dock ──
          Renders inline at the bottom of the editor body (sibling to the
          split layout, not viewport-fixed) so the modal's Save & Next /
          Back footer stays visible. The dock yields, not the commit
          actions: in a B2B publish flow, playback is supporting; the
          modal's CTAs are primary and must remain unobstructed. */}
      {generatedAudio && (
        <audio
          ref={audioRef}
          onLoadedMetadata={(e) =>
            setAudioDuration((e.target as HTMLAudioElement).duration)
          }
          onTimeUpdate={(e) =>
            setPlaybackProgress((e.target as HTMLAudioElement).currentTime)
          }
          onEnded={() => setIsPlaying(false)}
        />
      )}

      {showPlayer && generatedAudio && (
        <div className="tts-editor__player">
          <div className="d-flex align-items-center gap-3 tts-editor__player-left">
            <div
              className="d-flex align-items-center justify-content-center bg-primary text-white rounded-circle fw-bold tts-editor__player-avatar"
              style={{
                width: "40px",
                height: "40px",
                flexShrink: 0,
                background: generatedAudio.voiceColor,
              }}
            >
              {generatedAudio.voiceName.charAt(0)}
            </div>
            <div className="tts-editor__player-meta">
              <span className="m-0 fw-bold text-dark tts-editor__player-title">
                {generatedAudio.title}
              </span>
              <span className="tts-editor__player-sub">
                Generation {generatedAudio.generation}
              </span>
            </div>
          </div>

          <div className="tts-editor__player-controls">
            <button
              className="tts-editor__player-skip"
              onClick={() => {
                if (audioRef.current) {
                  audioRef.current.currentTime = Math.max(
                    0,
                    audioRef.current.currentTime - 10
                  );
                }
              }}
              title="Rewind 10s"
            >
              <span className="tts-editor__seek-btn">
                <i className="bi bi-arrow-counterclockwise" />
                <span>10</span>
              </span>
            </button>
            <button
              className="tts-editor__player-play-btn"
              onClick={handlePlayPause}
            >
              {isPlaying ? <Pause size={18} /> : <Play size={18} />}
            </button>
            <button
              className="tts-editor__player-skip"
              onClick={() => {
                if (audioRef.current) {
                  audioRef.current.currentTime = Math.min(
                    audioDuration,
                    audioRef.current.currentTime + 10
                  );
                }
              }}
              title="Forward 10s"
            >
              <span className="tts-editor__seek-btn">
                <i className="bi bi-arrow-clockwise" />
                <span>10</span>
              </span>
            </button>
          </div>

          <div className="tts-editor__player-progress">
            <span className="tts-editor__player-time">
              {formatTime(playbackProgress)}
            </span>
            <input
              type="range"
              className="tts-editor__player-bar"
              min="0"
              max={audioDuration}
              step="0.1"
              value={playbackProgress}
              onChange={(e) => {
                const t = Number(e.target.value);
                setPlaybackProgress(t);
                if (audioRef.current) audioRef.current.currentTime = t;
              }}
            />
            <span className="tts-editor__player-time">
              {formatTime(audioDuration)}
            </span>
          </div>

          <div className="tts-editor__player-voice-tag">
            <span
              className="tts-editor__voice-dot"
              style={{ background: generatedAudio.voiceColor }}
            />
            {/* <span>
              {generatedAudio.voiceName}
            </span> */}
          </div>

          <div className="tts-editor__player-actions">
            <button
              className="tts-editor__player-action-btn tts-editor__player-action-btn--close"
              onClick={() => {
                setIsPlaying(false);
                setShowPlayer(false);
              }}
              title="Minimize player"
            >
              <ChevronDown size={16} />
            </button>
          </div>
        </div>
      )}

      {/* Minimized player bar — show when player is hidden but audio exists */}
      {!showPlayer && generatedAudio && (
        <button
          className="tts-editor__player-minimized"
          onClick={() => setShowPlayer(true)}
        >
          <div
            className="tts-editor__player-minimized-avatar"
            style={{ background: generatedAudio.voiceColor }}
          >
            {generatedAudio.voiceName.charAt(0)}
          </div>
          <span className="tts-editor__player-minimized-title">
            {generatedAudio.title}
          </span>
          <span className="tts-editor__player-minimized-gen">
            Gen {generatedAudio.generation}
          </span>
          <ChevronUp size={16} className="tts-editor__player-minimized-icon" />
        </button>
      )}

      <AlertDialog
        isOpen={showStaleTextDialog}
        onClose={() => {
          setShowStaleTextDialog(false);
          if (staleDialogRegenerateChosen.current) {
            staleDialogRegenerateChosen.current = false;
          } else {
            handleNextProceed();
          }
        }}
        onConfirm={() => {
          staleDialogRegenerateChosen.current = true;
          handleGenerate();
        }}
        title="Unsaved Changes"
        message="Do you want to regenerate the speech based on latest changes?"
        confirmText="Yes, Regenerate"
        cancelText="No"
        confirmButtonType="primary"
      />
    </div>
  );
};

const mapState = (state: RootState) => ({
  title: state.media.mediaDetails.media?.title ?? "",
  Team: state.media.mediaDetails.media?.team ?? "",
  bunchProductList: state.constants.bunchProductList?.list ?? [],
  defaultTeam: state.constants.defaultTeam,
  aiVoices: state.media.aiVoices,
  aiLanguages: state.media.aiLanguages,
  aiTextToAudio: state.media.aiTextToAudio,
  aiTextToDialogue: state.media.aiTextToDialogue,
  singleCurrentUser: state.users.singleCurrentUser,
  productList: state.constants.productList?.list ?? [],
});

const mapDispatch = {
  getAiVoices,
  getAiLanguages,
  generateTextToAudio,
  generateTextToDialogue,
  callRegisterCustomMediaOnCms,
  showUploadProgress,
  addFile,
  emptyMediaDetails,
  emptyAssetDetails,
  getMedia,
};

const connector = connect(mapState, mapDispatch);
type ConnectedTTSProps = ConnectedProps<typeof connector>;

export default connector(TextToSpeechForm);
